import React, { useState } from 'react'
import styled from 'styled-components'
import Modal from 'components/atoms/Modal'
import { addToCart } from 'utils/localStorage'
import { redirect } from 'utils/paths'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart'
import MailOutlineIcon from '@mui/icons-material/MailOutline'
import CreditCardIcon from '@mui/icons-material/CreditCard'
import { createBillingPortalSession } from 'services/billingService'
import { toast } from 'react-toastify'

const DormantBpModal = ({
  open,
  onClose,
  bpId,
  bpName,
  paymentPastDue = false,
  canResolvePayment = true
}) => {
  const [isOpeningBillingPortal, setIsOpeningBillingPortal] = useState(false)

  const handleReactivate = () => {
    addToCart(bpId)
    redirect('/cart')
  }

  const handleResolvePayment = async () => {
    setIsOpeningBillingPortal(true)

    try {
      const response = await createBillingPortalSession()
      window.location.assign(response.data.data.url)
    } catch (error) {
      toast.error(error?.response?.data?.message || 'Could not open payment management.')
      setIsOpeningBillingPortal(false)
    }
  }

  return (
    <Modal
      open={open}
      title={paymentPastDue ? 'Subscription payment required' : 'Dormant Building Passport'}
      onClose={onClose}
    >
      <Content>
        <IconWrapper>
          <ErrorOutlineIcon />
        </IconWrapper>
        <BpName>{bpName || 'This Building Passport'}</BpName>
        <Message>
          {paymentPastDue
            ? canResolvePayment
              ? 'The latest subscription payment was unsuccessful. Access is suspended until the outstanding payment is completed.'
              : 'The latest subscription payment was unsuccessful. Access is suspended until the account holder who paid for this subscription resolves the outstanding invoice.'
            : 'This Building Passport is currently dormant and needs to be reactivated before it can be accessed.'}
        </Message>
        {(!paymentPastDue || canResolvePayment) && (
          <ReactivateButton
            onClick={paymentPastDue ? handleResolvePayment : handleReactivate}
            disabled={isOpeningBillingPortal}
          >
            {paymentPastDue ? <CreditCardIcon /> : <ShoppingCartIcon />}
            {isOpeningBillingPortal
              ? 'Opening secure billing...'
              : paymentPastDue
                ? 'Update payment and pay'
                : 'Reactivate Subscription'}
          </ReactivateButton>
        )}
        <Divider>
          <DividerLine />
          <DividerText>or</DividerText>
          <DividerLine />
        </Divider>
        <ContactRow>
          <MailOutlineIcon />
          <span>
            Contact our team at{' '}
            <ContactLink href='mailto:renewals@buildingpassport.com'>
              renewals@buildingpassport.com
            </ContactLink>
          </span>
        </ContactRow>
      </Content>
    </Modal>
  )
}

export default DormantBpModal

const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 8px 0;
`

const IconWrapper = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: #fffbe6;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 16px;

  svg {
    font-size: 28px;
    color: #faad14;
  }
`

const BpName = styled.h3`
  margin: 0 0 8px;
  font-size: 16px;
  font-weight: 600;
  color: ${({ theme }) => theme.colors.haiti};
`

const Message = styled.p`
  margin: 0 0 24px;
  font-size: 14px;
  line-height: 1.5;
  color: #4b5563;
`

const ReactivateButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  padding: 12px 24px;
  border: none;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.2s, transform 0.15s;

  svg {
    font-size: 18px;
  }

  &:hover {
    background: #b83d1f;
    transform: translateY(-1px);
  }

  &:active {
    transform: translateY(0);
  }
`

const Divider = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
  margin: 20px 0;
  gap: 12px;
`

const DividerLine = styled.div`
  flex: 1;
  height: 1px;
  background: #e5e7eb;
`

const DividerText = styled.span`
  font-size: 12px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const ContactRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.lightGrey};
  width: 100%;
  box-sizing: border-box;
  font-size: 13px;
  color: ${({ theme }) => theme.colors.haiti};

  svg {
    font-size: 18px;
    color: ${({ theme }) => theme.colors.grey[800]};
    flex-shrink: 0;
  }
`

const ContactLink = styled.a`
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`
